import React, { useCallback, useMemo } from 'react'

import {
  F0Box,
  F0Heading,
  SurveyAnsweringForm,
  type SurveyAnswers,
  type SurveyFormBuilderElement,
  type SurveyFormSubmitResult,
  type SurveySubmitAnswers,
} from '@factorialco/f0-react'
import { DetailsItemsList, CardSelectableContainer } from '@factorialco/f0-react/dist/experimental'
import type { ModuleId, CardSelectableItem } from '@factorialco/f0-react/dist/experimental'
import { File as FileIcon, Person as PersonIcon } from '@factorialco/f0-react/icons/app'
import { useApolloClient } from '@apollo/client'
import {
  VendorOnboardingTaskMyMembershipDocument,
  VendorOnboardingTaskStatusChangeDocument,
  NewInboxTasksDocument,
  ProcurementsApprovalTimelineDocument,
  TaskActivityDocument,
} from 'generated/resources/graphql'

import app from 'modules/core/store/models/app'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import { useSubscription } from 'modules/core/hooks/useSubscription'

import { FromQuestionnaireToForm } from 'modules/forms/components/newFormEditor/helpers'
import { useAnswerForm } from 'modules/forms/hooks/useAnswerForm'
import { useLoadForm } from 'modules/forms/hooks/useForms'
import {
  applySurveySubmitAnswersToForm,
  mapFormToQuestionnaire,
} from 'modules/forms/lib/surveyAnsweringFormHelpers'

import { useActivity } from 'modules/core/lib/activities/activity'

import { useEmployeeTableViewRow } from 'modules/inbox_tasks/activities/Task/utils/content'
import { TaskCustomComponent } from 'modules/inbox_tasks/lib/abstraction'
import ProcurementPurchaseRequestTaskContent from 'modules/inbox_tasks/tasks/approval/ProcurementPurchaseRequest'
import { SendToVendorInlineForm } from 'modules/inbox_tasks/tasks/vendorOnboardingTask/SendToVendorForm'

const PROCUREMENT_MODULE_ID: ModuleId = 'finance-spending'

const SectionTitle = ({ content }: { content: string }) => (
  <F0Heading as="h3" variant="heading" content={content} />
)

const VendorOnboardingTaskContent: TaskCustomComponent<'ProcurementVendorOnboardingTask'> = ({
  taskable,
}) => {
  const purchaseRequest = taskable?.purchaseRequest
  const owner = purchaseRequest?.requesterEmployee?.employeeName ?? undefined
  const requesterContent = useEmployeeTableViewRow({ employee: owner, title: 'Requester' })
  const { access } = useCurrentSession()
  const apolloClient = useApolloClient()
  const { closeActivity } = useActivity()
  const backendMode: 'internal' | 'external' | undefined =
    taskable?.completionMode === 'external' || taskable?.completionMode === 'internal'
      ? taskable.completionMode
      : undefined
  const [completionMode, setCompletionMode] = React.useState<'internal' | 'external'>(
    () => backendMode ?? 'internal'
  )

  React.useEffect(() => {
    if (!backendMode) return
    setCompletionMode(backendMode)
  }, [backendMode])

  const [isFormOpen, setIsFormOpen] = React.useState(false)
  const [isCompletedBySubscription, setIsCompletedBySubscription] = React.useState(false)
  const completedByMeRef = React.useRef(false)
  useSubscription(VendorOnboardingTaskStatusChangeDocument, {
    onData: ({ data: subscriptionData }) => {
      const changedId =
        subscriptionData.data?.procurementVendorOnboardingTaskMarkAsDone?.vendorOnboardingTask
          ?.vendorOnboardingTaskId
      if (String(changedId) === String(taskable?.id)) {
        setIsCompletedBySubscription(true)
        // Always refetch — the subscription fires AFTER MarkAsDone completes,
        // so the backend data is guaranteed to be up-to-date at this point
        apolloClient.refetchQueries({
          include: [
            TaskActivityDocument,
            NewInboxTasksDocument,
            ProcurementsApprovalTimelineDocument,
          ],
        })
        if (!completedByMeRef.current) {
          setIsFormOpen(false)
          app.setFlash({
            type: 'correct',
            message: i18n.t('tasks.types.procurement_complete_form.completed_by_another'),
          })
        }
      }
    },
  })

  const formId = taskable?.vendorOnboardingFormId ?? undefined
  const materializedFormId = taskable?.formsMaterializedFormId ?? undefined
  const vendorEmail = taskable?.vendorEmail ?? purchaseRequest?.vendor?.email ?? undefined

  const { data: membershipData } = useQuery(VendorOnboardingTaskMyMembershipDocument, {
    variables: {
      materializedFormId: materializedFormId ?? '',
      respondentId: access.id,
    },
    skip: !materializedFormId,
  })
  const membershipId = membershipData?.forms?.memberships?.[0]?.id ?? undefined

  const { form, loadingForm } = useLoadForm({
    formId,
    resourceType: 'Procurement::TaskForm',
  })

  const elements = useMemo<SurveyFormBuilderElement[]>(() => {
    if (!form) return []
    return FromQuestionnaireToForm(mapFormToQuestionnaire(form).elements)
  }, [form])

  const { saveAnswers } = useAnswerForm()

  const surveyAnswerDefaultValue = useCallback(
    (
      element: Extract<SurveyFormBuilderElement, { type: 'question' }>,
      answer: NonNullable<typeof membershipData>['forms']['memberships'][number]['answers'][number]
    ): SurveyAnswers[string] => {
      switch (element.question.type) {
        case 'text':
        case 'longText':
          return { type: element.question.type, value: answer.answer ?? null }
        case 'rating':
        case 'numeric':
          return {
            type: element.question.type,
            value: answer.answer ? Number(answer.answer) : null,
          }
        case 'select':
        case 'dropdown-single':
          return {
            type: element.question.type,
            value: answer.elementOptionIds?.[0] ?? answer.answer ?? null,
          }
        case 'multi-select':
        case 'dropdown-multi':
        case 'file':
          return { type: element.question.type, value: answer.elementOptionIds ?? [] }
        case 'checkbox':
          return { type: 'checkbox', value: answer.answer === 'true' }
        case 'date':
          return { type: 'date', value: answer.answer ? new Date(answer.answer) : null }
        case 'link':
          return { type: 'link', value: answer.answer ?? null }
      }
    },
    []
  )

  const defaultValues = useMemo<Partial<SurveyAnswers>>(() => {
    const existingAnswers = membershipData?.forms?.memberships?.[0]?.answers ?? []
    if (existingAnswers.length === 0) return {}

    const answersByElementId = new Map(existingAnswers.map((answer) => [answer.elementId, answer]))
    const result: Partial<SurveyAnswers> = {}

    const addQuestionDefaultValue = (
      element: Extract<SurveyFormBuilderElement, { type: 'question' }>
    ) => {
      const answer = answersByElementId.get(element.question.id)
      if (!answer) return

      result[element.question.id] = surveyAnswerDefaultValue(element, answer)
    }

    for (const element of elements) {
      if (element.type === 'question') {
        addQuestionDefaultValue(element)
        continue
      }

      for (const question of element.section.questions ?? []) {
        addQuestionDefaultValue({ type: 'question', question })
      }
    }

    return result
  }, [elements, membershipData, surveyAnswerDefaultValue])

  const handleSubmit = useCallback(
    async (answers: SurveySubmitAnswers): Promise<SurveyFormSubmitResult> => {
      if (isCompletedBySubscription) {
        return {
          success: false,
          errors: {
            form: i18n.t('tasks.types.procurement_complete_form.already_completed'),
          },
        }
      }

      if (!form || !membershipId) {
        return { success: false, errors: { form: i18n.t('common.error') } }
      }

      completedByMeRef.current = true

      applySurveySubmitAnswersToForm(form, answers)

      const saved = await saveAnswers({
        form,
        membershipId,
        onSuccess: () => {
          form?.resetFormAnswers()
          setIsFormOpen(false)
          setIsCompletedBySubscription(true)
          // Refetch is handled by the subscription handler, which fires
          // after MarkAsDone completes — ensuring backend data is up-to-date
        },
      })

      if (!saved) {
        completedByMeRef.current = false
        return { success: false }
      }

      return { success: true }
    },
    [form, membershipId, saveAnswers, isCompletedBySubscription]
  )

  const detailsRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const el = detailsRef.current
    if (!el) return

    const handler = (e: MouseEvent) => {
      const target = e.target instanceof HTMLElement ? e.target : null
      const anchor = target?.closest('a[href="#open-form"]')
      if (anchor) {
        e.preventDefault()
        setIsFormOpen(true)
      }
    }

    el.addEventListener('click', handler)
    return () => el.removeEventListener('click', handler)
  }, [setIsFormOpen])

  if (!taskable) return null

  const isCompleted = isCompletedBySubscription || taskable?.taskStatus === 'done'

  const statusText = (() => {
    switch (taskable.vendorOnboardingStatus) {
      case 'employee_responded':
        return i18n.t('tasks.types.vendor_onboarding.employee_responded')
      case 'vendor_responded':
        return i18n.t('tasks.types.vendor_onboarding.completed')
      default:
        return i18n.t('tasks.types.vendor_onboarding.pending')
    }
  })()

  const statusVariant =
    taskable.vendorOnboardingStatus === 'pending' ? ('neutral' as const) : ('positive' as const)

  const modeTabs: CardSelectableItem<'internal' | 'external'>[] = [
    {
      value: 'internal',
      title: i18n.t('tasks.types.vendor_onboarding.mode.internal'),
      avatar: { type: 'icon', icon: FileIcon },
    },
    {
      value: 'external',
      title: i18n.t('tasks.types.vendor_onboarding.mode.external'),
      avatar: { type: 'icon', icon: PersonIcon },
    },
  ]

  const formTitle = purchaseRequest?.description ?? ''

  // The form is always openable. In external mode or when the internal task is done it
  // opens in preview (read-only, no submit button). Only internal + not-done allows submit.
  const canSubmitForm = completionMode === 'internal' && !isCompleted

  const formDetailsContent = (
    <F0Box flexDirection="column" gap="sm" paddingY="sm">
      <SectionTitle content={i18n.t('tasks.types.vendor_onboarding.attached_form')} />
      <F0Box paddingY="sm">
        <DetailsItemsList
          ref={detailsRef}
          tableView
          details={[
            {
              title: i18n.t('tasks.types.vendor_onboarding.form_name'),
              content: {
                type: 'item' as const,
                text: formTitle,
                action: { type: 'navigate' as const, href: '#open-form' },
              },
            },
            {
              title: i18n.t('tasks.types.vendor_onboarding.status'),
              content: {
                type: 'status-tag' as const,
                text: statusText,
                variant: statusVariant,
              },
            },
          ]}
        />
      </F0Box>
    </F0Box>
  )

  return (
    <>
      <ProcurementPurchaseRequestTaskContent
        id={purchaseRequest?.purchaseRequestId ?? '0'}
        requesterContent={requesterContent}
        detailsContent={
          <F0Box flexDirection="column" gap="lg">
            <SectionTitle content={i18n.t('tasks.types.vendor_onboarding.mode.title')} />
            <F0Box paddingY="sm">
              <CardSelectableContainer
                items={modeTabs}
                value={completionMode}
                layout="horizontal"
                disabled={isCompleted}
                onChange={(value) => {
                  if (!value) return
                  setCompletionMode(value)
                }}
              />
            </F0Box>

            {completionMode === 'external' && (
              <F0Box flexDirection="column" gap="sm">
                <SendToVendorInlineForm
                  taskableId={taskable.id}
                  disabled={isCompleted}
                  vendorEmail={vendorEmail}
                  vendorMessage={taskable.vendorMessage ?? undefined}
                  onError={() => {
                    app.setFlash({
                      type: 'wrong',
                      message: i18n.t('tasks.types.vendor_onboarding.send_to_vendor.error'),
                    })
                  }}
                  onSuccess={() => {
                    app.setFlash({
                      type: 'correct',
                      message: i18n.t('tasks.types.vendor_onboarding.send_to_vendor.success'),
                    })
                    closeActivity()
                  }}
                />
              </F0Box>
            )}
            {completionMode === 'internal' && formDetailsContent}
          </F0Box>
        }
      />

      {isFormOpen && (
        <SurveyAnsweringForm
          elements={elements}
          mode="all-questions"
          title={form?.title ?? ''}
          description={form?.description ?? undefined}
          defaultValues={defaultValues}
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          loading={loadingForm}
          position="fullscreen"
          {...(canSubmitForm ? { onSubmit: handleSubmit } : { preview: true })}
          module={{
            id: PROCUREMENT_MODULE_ID,
            label: i18n.t('sidebar.finance_spending'),
            href: '/procurement',
          }}
          labels={{
            empty: {
              title: i18n.t('f0.survey_answering_form.labels.empty.title'),
              description: i18n.t('f0.survey_answering_form.labels.empty.description'),
              emoji: '📝',
            },
          }}
        />
      )}
    </>
  )
}

export default VendorOnboardingTaskContent
